export function buildDataUpdateTargets(leagues = [], limit = null) {
  const limitedLeagueId = Number(limit?.league_id);
  const hasSeasonLimit =
    limit?.season_id !== null &&
    limit?.season_id !== undefined &&
    limit?.season_id !== "" &&
    Number.isFinite(Number(limit.season_id));
  const seen = new Set();
  return leagues
    .filter(
      (league) =>
        !Number.isFinite(limitedLeagueId) ||
        Number(league?.id) === limitedLeagueId
    )
    .map((league) => ({
      league_id: Number(league?.id),
      league_name: league?.name ?? null,
      ...(hasSeasonLimit ? { season_id: Number(limit.season_id) } : {}),
    }))
    .filter((target) => {
      if (!Number.isFinite(target.league_id) || seen.has(target.league_id))
        return false;
      seen.add(target.league_id);
      return true;
    });
}

export function buildDataUpdatePlan(diagnoses = []) {
  const seasons = [];
  const safeTasks = [];
  const historicalGaps = [];
  const errors = [];
  const notRequired = [];

  for (const diagnosis of diagnoses) {
    if (!diagnosis?.success) {
      errors.push(diagnosis);
      continue;
    }
    if (diagnosis.excluded) {
      continue;
    }
    if (diagnosis.skipped) {
      notRequired.push(diagnosis);
      continue;
    }
    const season = {
      league_id: Number(diagnosis.league_id),
      league_name: diagnosis.league_name,
      season_id: Number(diagnosis.season_id),
      season_name: diagnosis.season_name,
      foundations: diagnosis.foundations || null,
      inventory: diagnosis.inventory || null,
      inventory_report: diagnosis.inventory_report || null,
      repair_preview: diagnosis.repair_preview || null,
      repair_tasks: diagnosis.repair_tasks || [],
      final_fixtures: Number(diagnosis.final_fixtures || 0),
      finalized_fixtures: Number(diagnosis.finalized_fixtures || 0),
      open_fixtures: Number(diagnosis.open_fixtures || 0),
      uncompiled_fixtures: Number(diagnosis.uncompiled_fixtures || 0),
      compiled_open_fixtures: Number(diagnosis.compiled_open_fixtures || 0),
      safe_tasks: diagnosis.safe_tasks || [],
      historical_gaps: diagnosis.historical_gaps || [],
    };
    seasons.push(season);
    for (const task of [...(diagnosis.safe_tasks || []), ...(diagnosis.repair_tasks || [])])
      safeTasks.push({ ...season, ...task });
    for (const gap of diagnosis.historical_gaps || [])
      historicalGaps.push({ ...season, ...gap });
  }

  return { seasons, safeTasks, historicalGaps, errors, notRequired };
}

export function diagnosisHasDataMaintenanceOpportunity(diagnosis) {
  return Boolean(
    diagnosis?.success && !diagnosis?.excluded && !diagnosis?.skipped && (
      (diagnosis.safe_tasks?.length || 0) > 0 ||
      (diagnosis.repair_tasks?.length || 0) > 0 ||
      (diagnosis.inventory?.incomplete || 0) > 0 ||
      (diagnosis.historical_gaps?.length || 0) > 0 ||
      diagnosis?.foundations?.foundation_status === "reference_available"
    )
  );
}

export function groupDataUpdateIssues(items = []) {
  const grouped = new Map();
  for (const item of items.filter(Boolean)) {
    const key = `${item.league_id ?? "unknown"}:${item.season_id ?? "unknown"}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        league_id: item.league_id ?? null,
        league_name: item.league_name || null,
        season_id: item.season_id ?? null,
        season_name: item.season_name || null,
        reason: item.message || item.reason || "unknown_error",
        endpoint: item.endpoint || null,
        http_status: item.http_status ?? null,
        content_type: item.content_type || null,
        response_type: item.response_type || null,
      });
    }
  }
  return [...grouped.values()];
}

export async function executeDataUpdatePlan(
  plan,
  {
    compileFixture,
    maintainSeason,
    evaluateFixture,
    finalizeFixture,
    inspectTask = async () => ({}),
    recordDiagnostic = async () => {},
    onProgress = () => {},
    shouldStop = () => false,
  }
) {
  const retryableError = (error) => {
    if (error?.retryable === false) return false;
    if (error?.retryable === true) return true;
    return ["timeout", "server_error", "invalid_response_type", "invalid_json", "request_error", "incomplete_processing"]
      .includes(error?.diagnosis?.response_type) || Number(error?.diagnosis?.http_status) >= 500;
  };
  const completed = [];
  const failed = [];
  const skipped = [];
  const blockedSeasons = new Map();
  const tasks = [
    ...plan.safeTasks.map((task) => ({ ...task, historical: false })),
    ...plan.historicalGaps.map((task) => ({ ...task, historical: true, repair_only: true })),
  ];
  for (let index = 0; index < tasks.length; index += 1) {
    if (shouldStop()) {
      for (const remaining of tasks.slice(index)) {
        skipped.push({
          ...remaining,
          reason: "cancelled",
          message: "Auf Wunsch nicht begonnen.",
        });
      }
      return {
        success: true,
        cancelled: true,
        completed,
        failed,
        skipped,
        historicalGaps: plan.historicalGaps,
      };
    }
    const task = tasks[index];
    onProgress({ current: index, total: tasks.length, task });
    const seasonKey = `${task.league_id}:${task.season_id}`;
    if (blockedSeasons.has(seasonKey) && !task.repair_only && !task.historical) {
      skipped.push({
        ...task,
        reason: "season_failed",
        message: blockedSeasons.get(seasonKey),
      });
      onProgress({ current: index + 1, total: tasks.length, task });
      continue;
    }
    let initialState = {};
    let latestState = {};
    const completedSteps = new Set();
    let phase = "inspect";
    let finalError = null;
    let retryCount = 0;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        latestState = await inspectTask(task) || {};
        if (attempt === 0) initialState = latestState;
        if (task.repair_only) {
          phase = "maintain";
          await maintainSeason(task);
          completedSteps.add("maintain");
          if (task.historical) {
            phase = "finalize";
            await finalizeFixture(task);
            completedSteps.add("finalize");
          }
          completed.push(task);
          finalError = null;
          break;
        }
        if (Number(latestState.status) === 5 || latestState.operation_state === "complete") {
          skipped.push({ ...task, reason: "already_complete" });
          finalError = null;
          break;
        }
        if (latestState.operation_state === "statistics_applied") {
          phase = "finalize";
          await finalizeFixture(task);
        } else {
          if (task.needs_compile && !latestState.compiled && !latestState.operation_state) {
            phase = "compile";
            await compileFixture(task);
            completedSteps.add("compile");
          }
          phase = "maintain";
          await maintainSeason(task);
          completedSteps.add("maintain");
          if (!task.historical) {
            phase = "evaluate";
            await evaluateFixture(task);
            completedSteps.add("evaluate");
          } else {
            phase = "finalize";
            await finalizeFixture(task);
          }
        }
        completed.push(task);
        finalError = null;
        break;
      } catch (error) {
        finalError = error;
        if (attempt === 0 && retryableError(error)) {
          retryCount = 1;
          continue;
        }
      }
    }
    if (finalError) {
      try { latestState = await inspectTask(task) || latestState; } catch { /* diagnostic best effort */ }
      const message = finalError?.message || String(finalError);
      const modelApplied = latestState.model_applied === true || ["model_applied", "statistics_applied", "complete"].includes(latestState.operation_state);
      const changesModel = !task.repair_only && !task.historical;
      const chronologyBlocking = changesModel && !modelApplied && !["finalize", "report"].includes(phase);
      const failure = {
        ...task, reason: "fixture_failed", message, phase,
        chronology_blocking: chronologyBlocking,
        retry_count: retryCount,
        ...(finalError?.diagnosis || {}),
      };
      failed.push(failure);
      if (chronologyBlocking) blockedSeasons.set(seasonKey, message);
      try {
        await recordDiagnostic({
          ...task, phase,
          error_class: finalError?.diagnosis?.response_type || finalError?.name || "technical",
          reason_code: finalError?.diagnosis?.reason_code || finalError?.code || "PROCESSING_FAILED",
          operation_state: latestState.operation_state || null,
          status_before: initialState.status,
          status_after: latestState.status,
          model_applied: modelApplied,
          retry_count: retryCount,
          error_message: message,
          completed_steps: [...completedSteps],
        });
      } catch { /* support logging must never block the run */ }
    }
    onProgress({ current: index + 1, total: tasks.length, task });
  }
  return {
    success: true,
    completed,
    failed,
    skipped,
    historicalGaps: plan.historicalGaps,
  };
}


// Counts describe the read-only inventory; they never change execution tasks.
export function summarizeDataInventories(seasons = []) {
  const relevant = seasons.filter(season => /^2026\/(?:2027|27)$/.test(season.season_name || ""));
  const reports = relevant.map(season => season.inventory_report).filter(Boolean);
  const summary = {};
  const products = {};
  for (const report of reports) {
    for (const [key, value] of Object.entries(report.summary || {})) summary[key] = (summary[key] || 0) + value;
    for (const [name, values] of Object.entries(report.products || {})) {
      products[name] ||= {};
      for (const [key, value] of Object.entries(values)) products[name][key] = (products[name][key] || 0) + value;
    }
  }
  return { summary, products, leagues_checked: reports.length, unavailable: relevant.length - reports.length,
    market_derived_missing: reports.reduce((sum, row) => sum + row.market_derived_missing, 0),
    legacy_perfect_game: reports.filter(row => row.legacy_perfect_game).length,
    invalid_records: reports.filter(row => row.record_status !== "valid").length };
}
